import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FileText, Loader2, Download, Mail, Sparkles, ArrowRight, RefreshCw, TrendingUp, GitCompare, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import authedFetch from '../lib/authedFetch';
import secureStorage from '../lib/secureStorage';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { HeroAccents, healthHeroTheme } from '../components/layout/HeroAccents';
import { getSectionTheme, getLightSectionTheme } from '../lib/sectionThemes';
import { RoadmapBrief } from '../components/board-brief/RoadmapBrief';
import { LossWaterfall } from '../components/value-conversion/LossWaterfall';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtUsd = (v) => {
  v = Number(v) || 0;
  const a = Math.abs(v);
  return a >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}K`;
};
const ENGINE_META = {
  maturity: { color: '#8b5cf6', route: '/maturity-health' },
  lei: { color: '#0ea5e9', route: '/score-trends?tab=lei' },
  pe: { color: '#6366f1', route: '/people-engine/trends' },
  roe: { color: '#ec4899', route: '/roe-health/deep-dives' },
  roi: { color: '#f59e0b', route: '/roi-health' },
  overall: { color: '#10b981', route: '/' },
};

// --- Narrative diff (sentence-level) — powers the History Compare view. ---
const splitSentences = (t) => ((t || '').replace(/\n+/g, ' ').match(/[^.!?]+[.!?]*/g) || [])
  .map((s) => s.trim()).filter(Boolean);
const normSentence = (s) => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
const diffSides = (current, previous) => {
  const cs = splitSentences(current);
  const ps = splitSentences(previous);
  const cset = new Set(cs.map(normSentence));
  const pset = new Set(ps.map(normSentence));
  return {
    current: cs.map((text) => ({ text, status: pset.has(normSentence(text)) ? 'same' : 'added' })),
    previous: ps.map((text) => ({ text, status: cset.has(normSentence(text)) ? 'same' : 'removed' })),
  };
};

export default function BoardBriefPage() {
  const navigate = useNavigate();
  const { user, viewAsOrg } = useAuth();
  const generatedBy = user?.name || user?.full_name || user?.email || 'Seerios user';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [autoSend, setAutoSend] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cohort, setCohort] = useState('all');
  const [cohorts, setCohorts] = useState([]);
  const [vc, setVc] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareIdx, setCompareIdx] = useState(1);
  const briefRef = useRef(null);

  const headers = useMemo(() => {
    const token = secureStorage.getItem('session_token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const scopeQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (cohort && cohort !== 'all') p.set('cohort_id', cohort);
    if (viewAsOrg?.id) p.set('organization_id', viewAsOrg.id);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [cohort, viewAsOrg]);

  const cohortLabel = useMemo(
    () => (cohort === 'all' ? null : (cohorts.find((c) => c.id === cohort)?.name || cohort)),
    [cohort, cohorts]);

  const loadBrief = useCallback(() => {
    return authedFetch(`${API_URL}/api/board-brief${scopeQuery}`, { headers: { Authorization: headers.Authorization } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setData(d)).catch(() => setData(null));
  }, [headers, scopeQuery]);

  const [roadmap, setRoadmap] = useState(null);
  useEffect(() => {
    const orgQ = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    authedFetch(`${API_URL}/api/maturity-assessment/roadmap${orgQ}`, { headers: { Authorization: headers.Authorization } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setRoadmap(d)).catch(() => setRoadmap(null));
  }, [headers, viewAsOrg]);

  useEffect(() => {
    const orgQ = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
    authedFetch(`${API_URL}/api/people-engine/cohorts${orgQ}`, { headers: { Authorization: headers.Authorization } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setCohorts(d?.cohorts || [])).catch(() => setCohorts([]));
  }, [headers, viewAsOrg]);

  // Value Conversion context — powers the Missing Middle Loss Waterfall on the pack.
  useEffect(() => {
    const h = { headers: { Authorization: headers.Authorization } };
    Promise.all([
      authedFetch(`${API_URL}/api/import/data/gap-analysis${scopeQuery}`, h).then((r) => (r.ok ? r.json() : null)),
      authedFetch(`${API_URL}/api/import/data/gap-analysis/value-conversion-context${scopeQuery}`, h).then((r) => (r.ok ? r.json() : null)),
    ]).then(([gap, ctx]) => {
      if (ctx?.potential_value) {
        setVc({ gapRows: Object.values(gap?.gap_analysis || {}), stages: ctx.journey_stages, potential: ctx.potential_value });
      } else setVc(null);
    }).catch(() => setVc(null));
  }, [headers, scopeQuery]);

  const regenerateOverall = async () => {
    setRegenerating(true);
    try {
      const body = { ...(cohort !== 'all' ? { cohort_id: cohort } : {}), ...(viewAsOrg?.id ? { organization_id: viewAsOrg.id } : {}) };
      const r = await authedFetch(`${API_URL}/api/board-brief/regenerate-overall`, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (d?.insight) {
        setData((prev) => ({ ...prev, engines: prev.engines.map((e) => e.engine === 'overall' ? { ...e, insight: d.insight, generated_at: d.generated_at } : e) }));
        loadHistory();
        toast.success('Executive summary rewritten.');
      }
    } catch {
      toast.error('Could not rewrite the summary.');
    } finally {
      setRegenerating(false);
    }
  };

  const loadHistory = useCallback(() => {
    authedFetch(`${API_URL}/api/board-brief/overall-history${scopeQuery}`, { headers: { Authorization: headers.Authorization } })
      .then((r) => (r.ok ? r.json() : null)).then((d) => setHistory(d?.history || [])).catch(() => setHistory([]));
  }, [headers, scopeQuery]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    loadBrief().finally(() => setLoading(false));
    authedFetch(`${API_URL}/api/board-brief/schedule`, { headers: { Authorization: headers.Authorization } })
      .then((r) => (r.ok ? r.json() : null)).then((s) => { if (s) { setAutoSend(!!s.enabled); if ((s.recipients || []).length) setRecipients(s.recipients.join(', ')); } }).catch(() => {});
  }, [headers, loadBrief]);

  const refreshReads = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await authedFetch(`${API_URL}/api/board-brief/refresh`, { method: 'POST', headers, body: JSON.stringify({}) });
      if (res.ok) {
        const d = await res.json();
        await loadBrief();
        toast.success(d.total ? `Refreshed ${d.total} read${d.total !== 1 ? 's' : ''} from the latest numbers.` : 'All reads are already current.');
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Could not refresh the reads.');
      }
    } catch { toast.error('Could not refresh the reads.'); }
    finally { setRefreshing(false); }
  }, [headers, loadBrief]);

  const saveSchedule = useCallback(async (enabled) => {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    await authedFetch(`${API_URL}/api/board-brief/schedule`, {
      method: 'POST', headers, body: JSON.stringify({ enabled, recipients: list }),
    });
    toast.success(enabled ? 'Monthly auto-send enabled.' : 'Monthly auto-send turned off.');
  }, [recipients, headers]);

  const buildPdf = useCallback(async () => {
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const imgW = pageW - margin * 2;
    const usableH = pageH - margin * 2;
    const gutter = 16;
    const colW = (imgW - gutter) / 2;
    // Force a LIGHT board pack for the PDF regardless of the on-screen theme.
    // Dark mode is applied via `home-dark-scope` on the <main> ancestor
    // (DashboardLayout) + !important CSS — NOT html/body `.dark` — so we
    // swap that ancestor class to `app-light` during capture, then restore.
    const mainEl = briefRef.current.closest('main');
    const prevMainClass = mainEl ? mainEl.className : null;
    if (mainEl && mainEl.classList.contains('home-dark-scope')) {
      mainEl.classList.remove('home-dark-scope');
      mainEl.classList.add('app-light');
    }
    const capture = async (el, targetW) => {
      // Temporary padding so html2canvas never clips the first/last line
      // or a title sitting flush against the block edge.
      const prevPad = el.style.padding;
      const prevBg = el.style.backgroundColor;
      el.style.padding = '8px 6px';
      el.style.backgroundColor = '#ffffff';
      let c;
      try {
        c = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', logging: false });
      } finally {
        el.style.padding = prevPad;
        el.style.backgroundColor = prevBg;
      }
      if (!c.width || !c.height) return null; // guard: skip zero-size blocks
      let w = targetW;
      let h = (c.height / c.width) * w;
      if (h > usableH) { h = usableH; w = (c.width / c.height) * h; }
      return { img: c.toDataURL('image/jpeg', 0.92), w, h };
    };
    try {
      await new Promise((r) => setTimeout(r, 90));
      let y = margin;
      const placeFull = (blk) => {
        if (!blk) return;
        if (y + blk.h > pageH - margin && y > margin) { pdf.addPage(); y = margin; }
        pdf.addImage(blk.img, 'JPEG', margin, y, blk.w, blk.h);
        y += blk.h + 12;
      };
      for (const child of Array.from(briefRef.current.children)) {
        if (child.getAttribute && child.getAttribute('data-pdf-grid')) {
          // Engine briefs — pack two-up per row to keep the board pack tight.
          const sections = Array.from(child.children);
          for (let i = 0; i < sections.length; i += 2) {
            const left = await capture(sections[i], colW);
            const right = sections[i + 1] ? await capture(sections[i + 1], colW) : null;
            const rowH = Math.max(left?.h || 0, right?.h || 0);
            if (rowH === 0) continue;
            if (y + rowH > pageH - margin && y > margin) { pdf.addPage(); y = margin; }
            if (left) pdf.addImage(left.img, 'JPEG', margin, y, left.w, left.h);
            if (right) pdf.addImage(right.img, 'JPEG', margin + colW + gutter, y, right.w, right.h);
            y += rowH + 14;
          }
        } else {
          placeFull(await capture(child, imgW));
        }
      }
    } finally {
      if (mainEl && prevMainClass != null) mainEl.className = prevMainClass;
    }
    return pdf;
  }, []);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    try { (await buildPdf()).save('board-brief.pdf'); }
    catch (err) { console.error('Board Brief PDF export failed:', err); toast.error('Could not export the PDF.'); }
    finally { setExporting(false); }
  }, [buildPdf]);

  const sendEmail = useCallback(async () => {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient.'); return; }
    setSending(true);
    try {
      const pdf = await buildPdf();
      const res = await authedFetch(`${API_URL}/api/board-brief/email`, {
        method: 'POST', headers,
        body: JSON.stringify({ recipients: list, pdf_base64: pdf.output('datauristring'), filename: 'board-brief.pdf', scope_label: data?.organization_name }),
      });
      if (res.ok) { toast.success(`Board Brief emailed to ${list.length} recipient(s).`); setShowEmail(false); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Could not send the brief.'); }
    } catch { toast.error('Could not send the brief.'); }
    finally { setSending(false); }
  }, [recipients, buildPdf, headers, data]);

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const theme = healthHeroTheme(isDark, getSectionTheme, getLightSectionTheme);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const engines = data?.engines || [];
  const anyRead = engines.some((e) => e.insight);

  const actions = (
    <div className="flex items-center gap-2 flex-wrap" data-testid="board-brief-actions">
          <select value={cohort} onChange={(e) => setCohort(e.target.value)} data-testid="board-brief-cohort"
            className="text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 bg-white max-w-[190px]">
            <option value="all">Whole organization</option>
            {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={refreshReads} disabled={refreshing} data-testid="board-brief-refresh"
            className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-all hover:-translate-y-0.5',
              refreshing ? 'opacity-60 border-slate-200 text-slate-400' : 'border-indigo-300 text-indigo-700 hover:border-indigo-500 hover:bg-indigo-50')}>
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button onClick={() => setShowEmail(true)} data-testid="board-brief-email"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-all hover:-translate-y-0.5">
            <Mail className="h-4 w-4" /> Email
          </button>
          <button onClick={exportPdf} disabled={exporting} data-testid="board-brief-export"
            className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-all hover:-translate-y-0.5',
              exporting ? 'opacity-60 border-slate-200 text-slate-400' : 'border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700')}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export PDF
          </button>
    </div>
  );

  return (
    <PageShell theme={theme} hero={(
      <PageHero title="Board Brief" titleHint="Health & Insights" icon={FileText} description="A one-page board pack across Maturity, Learning, Capability, Transfer and Financial — with the chain amplification/leakage each drives." theme={theme} actions={actions} testId="board-brief-hero"><HeroAccents /></PageHero>
    )}>
    <div className="max-w-[210mm] mx-auto px-4 py-2" data-testid="board-brief-page">
      {/* Printable board pack */}
      <div ref={briefRef} className="bg-white shadow-[0_20px_50px_rgba(0,0,0,0.08)] print:shadow-none rounded-sm px-8 py-8"
        style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
        <div className="flex items-end justify-between gap-3 border-b-2 border-slate-900 pb-4 mb-6" data-testid="board-brief-header">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-lg font-black tracking-tight text-slate-900 leading-tight">Seertech · Board Brief</p>
              <p className="text-xs text-slate-500 leading-tight">{data?.organization_name}{cohortLabel ? ` · ${cohortLabel}` : ''}</p>
            </div>
          </div>
          <div className="text-right text-[11px] text-slate-500 leading-snug">
            <p className="font-semibold text-slate-700">{new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            <p>Generated by {generatedBy}</p>
          </div>
        </div>
        {data?.top_scenarios?.length >= 2 ? (
          <div className="mb-6 rounded-sm border border-slate-200 bg-slate-50 p-4" data-testid="board-brief-scenario-compare">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-3">
              <GitCompare className="h-3.5 w-3.5" /> Competing interventions — trade-off view
            </p>
            <div className="grid grid-cols-2 gap-4">
              {data.top_scenarios.slice(0, 2).map((sc, i) => (
                <div key={i} className="rounded-sm bg-white border-l-2 pl-3 py-2" style={{ borderColor: i === 0 ? '#0ea5e9' : '#f43f5e' }} data-testid={`board-brief-scenario-col-${i}`}>
                  <p className="text-xs font-bold text-slate-800 truncate leading-relaxed pb-1" title={sc.name}>{sc.name}</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-xl font-medium tabular-nums text-slate-900 font-mono">{sc.scenario_pe}%</span>
                    <span className="text-[11px] font-semibold text-emerald-600">+{sc.lift_pts} pts</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">in {sc.horizon} mo · from {sc.base_pe}% base · {sc.cost_per_point != null ? `$${sc.cost_per_point >= 1000 ? `${Math.round(sc.cost_per_point / 1000)}k` : Math.round(sc.cost_per_point)}/pt` : '—'}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {vc && (
          <div className="mb-6" data-testid="board-brief-waterfall">
            <LossWaterfall gapRows={vc.gapRows} stages={vc.stages} potential={vc.potential} />
          </div>
        )}
        {data?.cohort_compare?.length === 2 && (
          <div className="mb-6 rounded-sm border border-slate-200 bg-slate-50 p-4" data-testid="board-brief-cohort-compare">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-3">
              <GitCompare className="h-3.5 w-3.5" /> Cohort compare — top performers
            </p>
            <div className="grid grid-cols-2 gap-4">
              {data.cohort_compare.map((c, i) => (
                <div key={i} className="rounded-sm bg-white border-l-2 pl-3 py-2" style={{ borderColor: i === 0 ? '#0ea5e9' : '#f43f5e' }} data-testid={`board-brief-cohort-col-${i}`}>
                  <p className="text-xs font-bold text-slate-800 truncate leading-relaxed pb-1" title={c.label}>{c.label}</p>
                  <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {[['Net ROI', `${c.roi}x`], ['Net benefit', fmtUsd(c.net_benefit)], ['Investment', fmtUsd(c.investment)]].map(([l, v]) => (
                      <div key={l}>
                        <p className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{l}</p>
                        <p className="text-sm font-medium font-mono text-slate-900">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7" data-pdf-grid="1">
          {engines.map((e) => {
            const meta = ENGINE_META[e.engine] || {};
            return (
              <section key={e.engine} className="break-inside-avoid" data-testid={`board-brief-${e.engine}`}>
                <div className="flex items-center gap-2 border-b border-slate-200 pb-1.5 mb-2.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
                  <h2 className="text-sm font-bold tracking-tight text-slate-900 uppercase">{e.label}</h2>
                  {e.engine === 'overall' && (
                    <button onClick={regenerateOverall} disabled={regenerating} data-testid="board-brief-regenerate-overall" data-html2canvas-ignore="true"
                      className="ml-auto print:hidden inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-700 disabled:opacity-50">
                      {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Rewrite
                    </button>
                  )}
                  {e.generated_at && e.engine !== 'overall' && <span className="text-[9px] text-slate-400 ml-auto shrink-0">{new Date(e.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
                </div>
                {e.metrics?.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-2.5" data-testid={`board-brief-metrics-${e.engine}`}>
                    {e.metrics.map((mt, i) => {
                      const isChain = mt.kind === 'chain';
                      const neg = isChain && (mt.pct ?? 0) < 0;
                      const accent = isChain ? (neg ? '#f43f5e' : '#10b981') : meta.color;
                      return (
                        <div key={i} className="border-l-2 pl-2" style={{ borderColor: accent }}>
                          <p className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{mt.label}</p>
                          <p className={cn('text-sm font-medium font-mono leading-tight', isChain ? (neg ? 'text-rose-600' : 'text-emerald-600') : 'text-slate-900')}>{mt.value}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                {e.insight ? (
                  <p className="text-[11px] leading-relaxed text-slate-700 whitespace-pre-line">{e.insight}</p>
                ) : (
                  <div className="flex items-center justify-between gap-2 print:hidden" data-html2canvas-ignore="true">
                    <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> No read yet.</p>
                    {meta.route && <button onClick={() => navigate(meta.route)} className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 shrink-0" data-testid={`board-brief-open-${e.engine}`}>Open board <ArrowRight className="h-3 w-3" /></button>}
                  </div>
                )}
                {e.engine === 'overall' && history.length > 1 && (
                  <div className="mt-2 print:hidden" data-testid="board-brief-history" data-html2canvas-ignore="true">
                    <div className="flex items-center gap-3 flex-wrap">
                      <button onClick={() => setShowHistory((v) => !v)} className="text-[10px] font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1" data-testid="board-brief-history-toggle">
                        <History className="h-3 w-3" /> {showHistory ? 'Hide' : 'Show'} previous summaries ({history.length - 1})
                      </button>
                      <button onClick={() => setShowCompare((v) => !v)} className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1" data-testid="board-brief-compare-toggle">
                        <GitCompare className="h-3 w-3" /> {showCompare ? 'Hide compare' : 'Compare with a past summary'}
                      </button>
                    </div>
                    {showHistory && (
                      <div className="mt-1.5 space-y-1.5 border-l-2 border-slate-200 pl-2.5">
                        {history.slice(1).map((h, i) => (
                          <div key={i} data-testid={`board-brief-history-item-${i}`}>
                            <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">{h.generated_at ? new Date(h.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</p>
                            <p className="text-[10px] leading-relaxed text-slate-500">{h.insight}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {showCompare && (() => {
                      const curInsight = e.insight || history[0]?.insight || '';
                      const curKey = normSentence(curInsight);
                      // Build comparable options: every past summary whose text
                      // differs from the one currently displayed (skip self-vs-self).
                      const options = history
                        .map((h, i) => ({ h, i }))
                        .filter(({ h }) => normSentence(h.insight || '') !== curKey);
                      const effectiveIdx = options.some((o) => o.i === compareIdx)
                        ? compareIdx
                        : (options[0]?.i ?? 1);
                      const past = history[effectiveIdx];
                      const diff = diffSides(curInsight, past?.insight || '');
                      const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
                      return (
                        <div className="mt-2.5 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="board-brief-compare-panel">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Narrative diff — what changed</p>
                            <label className="flex items-center gap-1.5 text-[10px] text-slate-500">
                              Compare against
                              <select value={effectiveIdx} onChange={(ev) => setCompareIdx(Number(ev.target.value))} data-testid="board-brief-compare-select"
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 max-w-[150px]">
                                {options.map(({ h, i }) => (
                                  <option key={i} value={i}>{fmtDateTime(h.generated_at)}</option>
                                ))}
                              </select>
                            </label>
                          </div>
                          {options.length === 0 ? (
                            <p className="text-[10px] text-slate-400" data-testid="board-brief-compare-empty">No earlier summary differs from the current one yet — rewrite the Overall brief to build a comparison.</p>
                          ) : (<>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-sm bg-white border-l-2 border-emerald-400 p-2.5" data-testid="board-brief-compare-current">
                              <p className="text-[9px] font-semibold uppercase tracking-widest text-emerald-600 mb-1">Current · {fmtDateTime(history[0]?.generated_at)}</p>
                              <p className="text-[10px] leading-relaxed text-slate-700">
                                {diff.current.map((s, i) => (
                                  <span key={i} className={s.status === 'added' ? 'bg-emerald-100 text-emerald-900 rounded-sm px-0.5' : ''}>{s.text}{' '}</span>
                                ))}
                              </p>
                            </div>
                            <div className="rounded-sm bg-white border-l-2 border-slate-300 p-2.5" data-testid="board-brief-compare-previous">
                              <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Previous · {fmtDateTime(past?.generated_at)}</p>
                              <p className="text-[10px] leading-relaxed text-slate-500">
                                {diff.previous.map((s, i) => (
                                  <span key={i} className={s.status === 'removed' ? 'bg-rose-100 text-rose-900 line-through decoration-rose-300 rounded-sm px-0.5' : ''}>{s.text}{' '}</span>
                                ))}
                              </p>
                            </div>
                          </div>
                          <p className="mt-2 text-[9px] text-slate-400 flex items-center gap-2">
                            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-100 border border-emerald-300" /> new in current</span>
                            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-100 border border-rose-300" /> dropped since</span>
                          </p>
                          </>)}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </section>
            );
          })}
        </div>
        <RoadmapBrief rm={roadmap} />
        <p className="mt-7 pt-3 border-t border-slate-200 text-[9px] leading-relaxed text-slate-400" data-testid="board-brief-legend">
          <span className="font-semibold text-slate-500">How we estimate $ impact:</span> each component's chain impact is its transfer efficiency (the next stage ÷ this stage) expressed as amplification (+) or leakage (−). The $ figure applies that rate to the value pool — total modelled financial impact — flowing through the chain; Financial shows realised net benefit. These are directional estimates for board discussion, not audited financials.
        </p>
      </div>

      {!anyRead && (
        <p className="text-xs text-slate-400 mt-4 text-center">Tip: open each health board and click "Generate summary" on the trend narrator — the reads land here automatically.</p>
      )}

      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-md" data-testid="board-brief-email-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-indigo-600" /> Email the Board Brief</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Recipients (comma-separated)</label>
            <Input value={recipients} onChange={(ev) => setRecipients(ev.target.value)} placeholder="name@company.com, other@company.com" data-testid="board-brief-recipients" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 mt-3">
            <div>
              <p className="text-sm font-semibold text-slate-700">Monthly auto-send</p>
              <p className="text-[11px] text-slate-400">Emails this Board Brief on the 1st of each month.</p>
            </div>
            <Switch checked={autoSend} onCheckedChange={(v) => { setAutoSend(v); saveSchedule(v); }} data-testid="board-brief-autosend" />
          </div>
          <DialogFooter>
            <button onClick={sendEmail} disabled={sending} data-testid="board-brief-send"
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white', sending ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700')}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
}
